import { Calendar, Mail, UserCog, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import type { UserInfo } from '@/services/api/lake'
import { intlLocale } from '@/services/i18n/languages'
import { UserBadges } from './UserBadges'

export function UserDetailCard({ user, onEdit }: { user: UserInfo | null; onEdit: () => void }) {
  const { t, i18n } = useTranslation()

  if (!user) {
    return (
      <Card className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <Users className="h-10 w-10 mb-3 opacity-20" />
        <p className="text-sm">{t('admin.users.selectUser')}</p>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="max-md:grid-cols-1!">
        <div className="flex items-center gap-3 md:gap-4">
          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-lg font-bold text-muted-foreground uppercase flex-shrink-0 md:w-14 md:h-14 md:text-2xl">
            {user.username[0]}
          </div>
          <div className="max-md:min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <CardTitle className="text-lg max-md:break-all">{user.username}</CardTitle>
              <UserBadges user={user} size="md" />
            </div>
            {user.full_name && <CardDescription>{user.full_name}</CardDescription>}
          </div>
        </div>
        <CardAction className="max-md:col-start-1 max-md:row-span-1 max-md:row-start-auto max-md:justify-self-stretch">
          <Button
            variant="outline"
            size="sm"
            className="h-9 text-xs max-md:w-full md:h-8"
            onClick={onEdit}
          >
            <UserCog className="h-3.5 w-3.5 mr-1.5" />
            {t('admin.users.editUser')}
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <Separator className="mb-4" />
        <div className="space-y-2.5">
          {user.email && (
            <div className="flex items-center gap-3 text-sm">
              <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="text-muted-foreground max-md:min-w-0 max-md:break-all">
                {user.email}
              </span>
            </div>
          )}
          <div className="flex items-center gap-3 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="text-muted-foreground">
              {t('admin.users.memberSince')}{' '}
              {new Date(user.created_at).toLocaleDateString(intlLocale(i18n.language), {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
